import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchAdminEvents, type AdminEvent } from '@/api/adminApi';
import EventEditor from './EventEditor';
import { input } from '@/components/ui/design';
import { EmptyRow, Pager, SearchBox, formatDate, pill, rowBorder, smallBtn, tableWrap, td, th, useDebounced } from './shared';

const LIMIT = 20;
const STATUSES = ['', 'draft', 'published', 'completed'];

export default function EventsTab() {
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [deleted, setDeleted] = useState(false);
    const [editing, setEditing] = useState<AdminEvent | null>(null);
    const debounced = useDebounced(search.trim());
    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin-events', page, debounced, status, deleted],
        queryFn: () => fetchAdminEvents({ page, limit: LIMIT, search: debounced || undefined, status: status || undefined, deleted: deleted || undefined }),
        placeholderData: previous => previous,
    });

    return (
        <div>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
                <SearchBox value={search} onChange={value => { setSearch(value); setPage(1); }} placeholder="Search events by title" />
                <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className={`${input} md:max-w-[200px]`} aria-label="Status">
                    {STATUSES.map(s => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : 'All statuses'}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm font-semibold text-grey20 dark:text-nightTextSecondary">
                    <input type="checkbox" checked={deleted} onChange={e => { setDeleted(e.target.checked); setPage(1); }} className="h-4 w-4" />
                    Deleted events
                </label>
            </div>
            {isError && <p className="mb-3 text-sm text-red-500">Failed to load events.</p>}
            <div className={tableWrap}>
                <table className="w-full min-w-[760px]">
                    <thead>
                        <tr><th className={th}>Event</th><th className={th}>Organization</th><th className={th}>Starts</th><th className={th}>Status</th><th className={th}>Created</th><th className={th} /></tr>
                    </thead>
                    <tbody>
                        {(data?.data ?? []).map(event => (
                            <tr key={event._id} className={rowBorder}>
                                <td className={td}>
                                    <Link to={`/event/${event._id}`} className="font-semibold hover:underline">{event.title || 'Untitled'}</Link>
                                    {event.venue && <div className="text-grey40 dark:text-nightMuted">{event.venue}</div>}
                                </td>
                                <td className={td}>{event.organizerName ?? <span className="text-grey40 dark:text-nightMuted">None</span>}</td>
                                <td className={td}>{formatDate(event.startDate)}</td>
                                <td className={td}><span className={`${pill} capitalize`}>{event.status || 'draft'}</span></td>
                                <td className={td}>{formatDate(event.createdAt)}</td>
                                <td className={`${td} text-right`}><button type="button" className={smallBtn} onClick={() => setEditing(event)}>Edit</button></td>
                            </tr>
                        ))}
                        {(data?.data ?? []).length === 0 && <EmptyRow colSpan={6} loading={isLoading} text="No events found." />}
                    </tbody>
                </table>
            </div>
            <Pager page={page} limit={LIMIT} total={data?.total ?? 0} onPage={setPage} />
            {editing && <EventEditor eventId={editing._id} onClose={() => setEditing(null)} />}
        </div>
    );
}
